import React from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Edit, Trash2, Link as LinkIcon, Calendar, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '../../../utils/formatters'
import {
  STATUS_STYLES,
  PRIORITY_STYLES,
  STATUS_ICONS,
  isOverdue,
  isClosedStatus,
} from '../lib/actionItemStyles'

const rowClasses = (item: any): string => {
  if (isClosedStatus(item.status)) return 'bg-muted/30 text-muted-foreground'
  if (isOverdue(item)) return 'bg-red-50/60 border-l-4 border-l-red-500'
  if (item.status === 'OPEN') return 'bg-amber-50/40'
  return ''
}

interface Props {
  items: any[]
  showMeetingColumn?: boolean
  onEdit?: (id: string) => void
  onDelete?: (id: string) => void
  onOpenMeeting?: (id: string) => void
}

const ActionItemsTable: React.FC<Props> = ({
  items,
  showMeetingColumn,
  onEdit,
  onDelete,
  onOpenMeeting,
}) => {
  const { t } = useTranslation()

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('ActionItem_field_title')}</TableHead>
            {showMeetingColumn && <TableHead>{t('ActionItem_field_meeting')}</TableHead>}
            <TableHead>{t('ActionItem_field_assignee')}</TableHead>
            <TableHead>{t('ActionItem_field_due_date')}</TableHead>
            <TableHead>{t('ActionItem_field_priority')}</TableHead>
            <TableHead>{t('ActionItem_origin')}</TableHead>
            <TableHead>{t('ActionItem_field_status')}</TableHead>
            <TableHead>{t('ActionItem_field_linked')}</TableHead>
            <TableHead className="text-right">{t('Common_actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((ai) => {
            const StatusIcon = STATUS_ICONS[ai.status as keyof typeof STATUS_ICONS]
            return (
              <TableRow key={ai.id} className={cn(rowClasses(ai))}>
                <TableCell>
                  <span className={cn(isClosedStatus(ai.status) && 'line-through')}>
                    {ai.title}
                  </span>
                  {ai.agendaItem && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {t('ActionItem_agenda_label', { title: ai.agendaItem.title })}
                    </div>
                  )}
                </TableCell>
                {showMeetingColumn && (
                  <TableCell>
                    {ai.meeting ? (
                      <button
                        className="text-sm text-primary hover:underline"
                        onClick={() => onOpenMeeting?.(ai.meeting.id)}
                      >
                        {ai.meeting.title}
                      </button>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                    {ai.meeting?.scheduledAt && (
                      <div className="text-xs text-muted-foreground">
                        {formatDate(ai.meeting.scheduledAt)}
                      </div>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  {ai.assignedTo ? (
                    <span className="flex items-center gap-1 text-sm">
                      <User className="h-3 w-3" />
                      {ai.assignedTo.name}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <span
                    className={cn(
                      'flex items-center gap-1 text-sm',
                      isOverdue(ai) && 'font-medium text-red-700'
                    )}
                  >
                    <Calendar className="h-3 w-3" />
                    {ai.dueDate ? formatDate(ai.dueDate) : '—'}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={
                      PRIORITY_STYLES[ai.priority as keyof typeof PRIORITY_STYLES] || ''
                    }
                  >
                    {t(`ActionItem_priority_${(ai.priority || 'MEDIUM').toLowerCase()}`)}
                  </Badge>
                </TableCell>
                <TableCell>
                  {ai.originOccurrenceNumber != null ? (
                    <Badge variant="outline" className="text-xs">
                      #{ai.originOccurrenceNumber}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={
                      STATUS_STYLES[ai.status as keyof typeof STATUS_STYLES] || ''
                    }
                  >
                    {StatusIcon && <StatusIcon className="h-3 w-3 mr-1" />}
                    {t(`ActionItem_status_${ai.status.toLowerCase()}`)}
                  </Badge>
                </TableCell>
                <TableCell>
                  {ai.linkedSnag ? (
                    <span className="flex items-center gap-1 text-sm">
                      <LinkIcon className="h-3 w-3" />
                      {ai.linkedSnag.title}
                    </span>
                  ) : ai.linkedDoc ? (
                    <span className="flex items-center gap-1 text-sm">
                      <LinkIcon className="h-3 w-3" />
                      {ai.linkedDoc.name}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {onEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(ai.id)}
                      title={t('Common_edit')}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(ai.id)}
                      title={t('Common_delete')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

export default ActionItemsTable
